import { lazy, useEffect, useState, type FormEvent } from "react";
import { api, Button, defineWebModule, OPERATING_MODEL_LABEL, OPERATING_MODEL_SHORT, PageBanner, SectionCard, StatusPill, Timeline, ukDate, ukDateTime, useShell } from "@tc/ui";
import { manifest } from "./manifest.js";
import { useConfirm } from "./web/confirm.js";
import { Entities, SiteEntity } from "./web/Entities.js";
import { AddFromGoogle, SiteGoogle } from "./web/GoogleDetails.js";
import type { Details } from "./google.js";

interface Site {
  code: string; name: string; kind: "site" | "head_office"; epos_system: string; active: boolean; external_ids: { system: string; id: string }[];
  model: string | null; model_since: string | null; next_model: { model: string; from: string } | null;
  postcode: string | null; lat: number | null; lng: number | null;
  google: { diffs: number; closed: boolean; read_at: string } | null;
}
interface History { periods: { model: string; from: string; until: string | null; by: string; reason: string; at: string }[]; log: { model: string | null; from: string; action: string; reason: string; by: string; at: string }[] }

/** Today in the UK (toISOString() is the UTC date: a day behind between 00:00 and 01:00 in summer). */
const ukToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date());
const MODEL_STATUS: Record<string, "info" | "success" | "warning"> = { commission: "info", operator: "success", company: "warning" };
const EFFECT: Record<string, string> = {
  commission: "The operator runs the site as their own business. Commission statements and invoices apply; the site appears in Commission.",
  operator: "The company owns stock, sales and waste. The operator staffs the site and signs off like a store manager, paid a weekly management fee.",
  company: "Run directly by the company with its own managers and staff. No commission or operator fees.",
};

function ModelPill({ model }: { model: string | null }) {
  return model ? <StatusPill status={MODEL_STATUS[model] ?? "info"}>{OPERATING_MODEL_SHORT[model] ?? model}</StatusPill> : <StatusPill status="warning">Not set</StatusPill>;
}

function SitesPage() {
  const { can } = useShell();
  const [sites, setSites] = useState<Site[] | null>(null);
  // A task ("Google listing differs") opens the site straight away: /sites?site=CODE.
  const [open, setOpen] = useState<string | null>(new URLSearchParams(window.location.search).get("site"));
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ code: "", name: "", epos_system: "cbe", kind: "site" });
  const [google, setGoogle] = useState<{ placeId: string; name: string; details: Details } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [entities, setEntities] = useState(new URLSearchParams(window.location.search).get("panel") === "entities");
  const load = () => api<{ sites: Site[] }>("/api/m/sites").then((d) => setSites(d.sites), (e: Error) => setError(e.message));
  useEffect(() => { void load(); }, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api("/api/m/sites", { body: { ...form, ...(google ? { google_place_id: google.placeId, details: google.details } : {}) } });
      setForm({ code: "", name: "", epos_system: "cbe", kind: "site" }); setGoogle(null); setFormKey(formKey + 1); void load();
    } catch (err) { setError((err as Error).message); }
  }
  const counts = (m: string) => sites?.filter((s) => s.kind !== "head_office" && s.model === m).length ?? 0;
  const offices = sites?.filter((s) => s.kind === "head_office").length ?? 0;
  const site = sites?.find((s) => s.code === open);

  return (
    <>
      <PageBanner title="Sites" subtitle={sites ? `${sites.length - offices} sites${offices ? ` and ${offices === 1 ? "a head office" : `${offices} head offices`}` : ""}` : "Loading…"}
        actions={<button className="tc-btn-banner" onClick={() => setEntities(!entities)}>Managing companies</button>}>
        {sites && <div className="tc-banner-stats">
          <span><b>{counts("commission")}</b>commission</span><span><b>{counts("operator")}</b>operator managed</span><span><b>{counts("company")}</b>company</span>
          {offices > 0 && <span><b>{offices}</b>head office</span>}
        </div>}
      </PageBanner>
      <div className="tc-content">
        {error && <div className="tc-error" role="alert">{error}</div>}
        {entities && <div className="tc-grid"><Entities onClose={() => { setEntities(false); window.history.replaceState(null, "", "/sites"); }} /></div>}
        <div className="tc-grid">
          <SectionCard title="All sites" span={site ? 6 : 8}>
            <div className="tc-mini-list">
              {(sites ?? []).map((s) => (
                <button key={s.code} className={`tc-mini-row${s.code === open ? " on" : ""}`} onClick={() => setOpen(s.code === open ? null : s.code)}>
                  <span><b>{s.name}</b> <span className="mono muted tc-small">{s.code}</span><br /><span className="muted tc-small">{s.kind === "head_office" ? "Head office: no fuel, pricing or sales" : s.epos_system.toUpperCase()}{s.external_ids.length ? ` · ${linkNames(s.external_ids.map((x) => x.system))}` : ""}{s.kind !== "head_office" && s.model_since ? ` · since ${ukDate(s.model_since)}` : ""}{s.kind !== "head_office" && s.next_model ? ` · ${OPERATING_MODEL_SHORT[s.next_model.model]} from ${ukDate(s.next_model.from)}` : ""}</span></span>
                  <span className="tc-row-pills">{s.kind === "head_office" ? <StatusPill status="info">Head office</StatusPill> : <ModelPill model={s.model} />}{!s.active && <StatusPill status="warning">Closed</StatusPill>}
                    {s.google?.closed ? <StatusPill status="critical">Google: closed</StatusPill> : s.google?.diffs ? <StatusPill status="warning">Google differs</StatusPill> : null}</span>
                </button>
              ))}
            </div>
          </SectionCard>
          {site ? <SitePanel site={site} canChange={can("sites.model")} onChanged={load} onClose={() => setOpen(null)} /> : can("sites.manage") && (
            <SectionCard title="Add a site" span={4}>
              <form onSubmit={add} className="tc-setup-form tc-p0">
                <AddFromGoogle key={formKey} name={form.name} onPicked={(p) => { setGoogle(p); if (p && !form.name.trim()) setForm({ ...form, name: p.name }); }} />
                <label className="tc-field">Site code<input className="tc-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. SITE1" required /></label>
                <label className="tc-field">Name<input className="tc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Park Lane" required /></label>
                <label className="tc-field">EPOS system
                  <select className="tc-input" value={form.epos_system} onChange={(e) => setForm({ ...form, epos_system: e.target.value })}>
                    <option value="cbe">CBE</option><option value="vme">VME</option><option value="madic">Madic</option><option value="other">Other</option>
                  </select>
                </label>
                <label className="tc-field">What is it?
                  <select className="tc-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                    <option value="site">Trading site</option><option value="head_office">Head office</option>
                  </select>
                </label>
                <Button primary type="submit">Add site</Button>
                <p className="muted tc-small tc-m0">How it's run, wholesaler and the rest are asked in the setup checklist{google ? "" : ", and the address and opening hours too unless you find it on Google"}.</p>
              </form>
            </SectionCard>
          )}
        </div>
      </div>
    </>
  );
}

/** One site: how it's run now, the full dated history, and an audited change. */
function SitePanel({ site, canChange, onChanged, onClose }: { site: Site; canChange: boolean; onChanged: () => void; onClose: () => void }) {
  const [h, setH] = useState<History | null>(null);
  const [change, setChange] = useState({ model: "", from: ukToday(), reason: "" });
  const ask = useConfirm();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => api<History>(`/api/m/sites/site/${site.code}/models`).then(setH);
  useEffect(() => { void load(); setChange({ model: "", from: ukToday(), reason: "" }); setMsg(null); }, [site.code]);
  const todayIso = ukToday();

  async function save() {
    setMsg(null);
    try {
      await api(`/api/m/sites/site/${site.code}/model`, { body: change });
      setMsg({ ok: true, text: `Saved. ${site.name} is ${OPERATING_MODEL_SHORT[change.model]?.toLowerCase()} from ${ukDate(change.from)}.` });
      setChange({ ...change, model: "", reason: "" });
      await load(); onChanged();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }

  return (
    <SectionCard title={<>{site.name} <span className="mono muted tc-small">{site.code}</span></>} span={6} action={<button className="tc-link" onClick={onClose}>Close</button>}>
      <SiteKind site={site} canChange={canChange} onChanged={onChanged} />
      {ask.sheet}
      {site.kind === "head_office" ? (
        <div className="tc-model-now">
          <span className="muted tc-small">How it's run</span>
          <p className="muted tc-small tc-m0">A head office isn't run on an operating model: no commission, operator fee, rent or charges.{h?.periods.length ? " The model set before it became a head office is kept but ignored." : ""}</p>
        </div>
      ) : (
        <div className="tc-model-now">
          <span className="muted tc-small">How it's run today</span>
          <div><ModelPill model={site.model} />{site.model_since && <span className="muted tc-small"> since {ukDate(site.model_since)}</span>}</div>
          {site.model && <p className="muted tc-small tc-m0">{EFFECT[site.model]}</p>}
        </div>
      )}
      {msg && <div className={msg.ok ? "tc-ok tc-mt" : "tc-error tc-mt"}>{msg.text}</div>}
      {canChange && site.kind !== "head_office" && (
        <div className="tc-model-change">
          <h3 className="tc-h3">Change how it's run</h3>
          <div className="tc-model-options">
            {(["commission", "operator", "company"] as const).map((m) => (
              <button key={m} type="button" className={`tc-model-opt${change.model === m ? " on" : ""}`} disabled={m === site.model && !site.next_model} onClick={() => setChange({ ...change, model: m })}>
                <b>{OPERATING_MODEL_LABEL[m]}</b><span>{EFFECT[m]}</span>
              </button>
            ))}
          </div>
          {change.model && (
            <div className="tc-form-row tc-mt">
              <label className="tc-field">From<input className="tc-input" type="date" value={change.from} onChange={(e) => setChange({ ...change, from: e.target.value })} /></label>
              <label className="tc-field tc-grow">Reason (kept in the history)<input className="tc-input" value={change.reason} onChange={(e) => setChange({ ...change, reason: e.target.value })} placeholder="e.g. Operator agreement ended; taken back in house" /></label>
              <Button primary disabled={change.reason.trim().length < 4} onClick={() => {
                ask.confirm(`${site.name} becomes ${OPERATING_MODEL_LABEL[change.model]} from ${ukDate(change.from)}?`, () => void save(), { yes: "Save change", text: change.from < todayIso ? "It's backdated: drafts for those dates will be worked out again; signed-off invoices don't change." : undefined });
              }}>Save change</Button>
            </div>
          )}
        </div>
      )}
      <SiteEntity code={site.code} canChange={canChange} />
      <SiteName site={site} canChange={canChange} onChanged={onChanged} />
      <SiteLocation site={site} canChange={canChange} onChanged={onChanged} />
      <SiteGoogle code={site.code} name={site.name} onChanged={onChanged} />
      <SiteProjects code={site.code} />
      <h3 className="tc-h3">History</h3>
      {h && <Timeline items={h.log.map((l) => ({
        time: ukDateTime(l.at),
        status: l.action === "set" ? "success" as const : l.action === "cancelled" ? "warning" as const : "info" as const,
        title: `${l.action === "set" ? "Set to" : l.action === "replaced" ? "Replaced" : "Cancelled"} ${l.model ? OPERATING_MODEL_SHORT[l.model] : ""} from ${ukDate(l.from)}`,
        detail: `${l.by}${l.reason ? ` · ${l.reason}` : ""}`,
      }))} />}
      {canChange && h?.periods.filter((p) => p.from > todayIso).map((p) => (
        <div key={p.from} className="tc-info tc-mt">Scheduled: {OPERATING_MODEL_SHORT[p.model]} from {ukDate(p.from)}. <button className="tc-link" onClick={async () => { await api(`/api/m/sites/site/${site.code}/model/${p.from}`, { method: "DELETE" }); await load(); onChanged(); }}>Cancel it</button></div>
      ))}
    </SectionCard>
  );
}

const KIND_EFFECT = {
  site: "A forecourt that trades: fuel, pricing, sales, cash office and everything else that applies to how it's run.",
  head_office: "Head office for the other sites. Staff, rotas, training, chat, maintenance and projects work as usual; it's left out of fuel, pricing, sales reports, cash office, commission and site checks.",
} as const;

/** Trading site or head office: shown to everyone, changed by company admins (audited, with a reason). */
function SiteKind({ site, canChange, onChanged }: { site: Site; canChange: boolean; onChanged: () => void }) {
  const [want, setWant] = useState<"site" | "head_office">(site.kind);
  const [reason, setReason] = useState("");
  const [log, setLog] = useState<{ kind: string; reason: string; by: string; at: string }[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => api<{ log: typeof log }>(`/api/m/sites/site/${site.code}/kind`).then((d) => setLog(d.log), () => setLog([]));
  useEffect(() => { setWant(site.kind); setReason(""); setMsg(null); void load(); }, [site.code, site.kind]);
  const save = async () => {
    setMsg(null);
    try {
      await api(`/api/m/sites/site/${site.code}/kind`, { method: "PUT", body: { kind: want, reason } });
      setMsg({ ok: true, text: want === "head_office" ? `${site.name} is now a head office.` : `${site.name} is a trading site again.` });
      setReason(""); onChanged();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  return (
    <div className="tc-mb">
      <h3 className="tc-h3">What is it?</h3>
      {msg && <div className={msg.ok ? "tc-ok" : "tc-error"} role={msg.ok ? "status" : "alert"}>{msg.text}</div>}
      {canChange ? (
        <>
          <div className="tc-model-options">
            {(["site", "head_office"] as const).map((k) => (
              <button key={k} type="button" className={`tc-model-opt${want === k ? " on" : ""}`} onClick={() => setWant(k)}>
                <b>{k === "site" ? "Trading site" : "Head office"}</b><span>{KIND_EFFECT[k]}</span>
              </button>
            ))}
          </div>
          {want !== site.kind && (
            <div className="tc-form-row tc-mt">
              <label className="tc-field tc-grow">Reason (kept in the history)<input className="tc-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Our office, not a forecourt" /></label>
              <Button primary disabled={reason.trim().length < 4} onClick={() => void save()}>Save</Button>
            </div>
          )}
        </>
      ) : <p className="muted tc-small tc-m0"><b>{site.kind === "head_office" ? "Head office" : "Trading site"}.</b> {KIND_EFFECT[site.kind]}</p>}
      {log.length > 0 && <p className="muted tc-small tc-m0">{log.map((l) => `${l.kind === "head_office" ? "Made a head office" : "Made a trading site"} ${ukDateTime(l.at)} by ${l.by}${l.reason ? ` (${l.reason})` : ""}`).join(" · ")}</p>}
    </div>
  );
}

const ProfilePage = lazy(() => import("./web/ProfilePage.js").then((m) => ({ default: m.ProfilePage })));
const RatingsPage = lazy(() => import("./web/RatingsPage.js").then((m) => ({ default: m.RatingsPage })));

export default defineWebModule({ manifest, pages: { "": SitesPage, "/ratings": RatingsPage, "/profile": ProfilePage } });

/** The site's name, shown on every page (the code behind it never changes). */
function SiteName({ site, canChange, onChanged }: { site: Site; canChange: boolean; onChanged: () => void }) {
  const [name, setName] = useState(site.name);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { setName(site.name); setMsg(null); }, [site.code, site.name]);
  const save = async () => {
    try {
      const r = await api<{ name: string }>(`/api/m/sites/site/${site.code}/name`, { method: "PUT", body: { name: name.trim() } });
      setMsg({ ok: true, text: `Renamed to ${r.name}. Every page shows the new name.` }); onChanged();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  return (
    <div className="tc-mt">
      <h3 className="tc-h3">Name</h3>
      {msg && <div className={msg.ok ? "tc-ok" : "tc-error"} role={msg.ok ? "status" : "alert"}>{msg.text}</div>}
      <div className="tc-form-row">
        <label className="tc-field tc-grow">Name<input className="tc-input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} disabled={!canChange} /></label>
        {canChange && <Button onClick={() => void save()} disabled={name.trim().length < 2 || name.trim() === site.name}>Save</Button>}
      </div>
    </div>
  );
}

/** The site's postcode, which places it on the map for its weather (Fuel Finder's position is used if there's none). */
function SiteLocation({ site, canChange, onChanged }: { site: Site; canChange: boolean; onChanged: () => void }) {
  const [pc, setPc] = useState(site.postcode ?? "");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { setPc(site.postcode ?? ""); setMsg(null); }, [site.code, site.postcode]);
  const save = async () => {
    try {
      const r = await api<{ postcode: string | null; lat: number | null }>(`/api/m/sites/site/${site.code}/location`, { method: "PUT", body: { postcode: pc.trim() || null } });
      setMsg({ ok: true, text: r.lat !== null ? `Saved: ${r.postcode ?? "no postcode"}, on the map.` : "Saved. It'll be placed on the map overnight." }); onChanged();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  return (
    <div className="tc-mt">
      <h3 className="tc-h3">Location</h3>
      {msg && <div className={msg.ok ? "tc-ok" : "tc-error"} role={msg.ok ? "status" : "alert"}>{msg.text}</div>}
      <div className="tc-form-row">
        <label className="tc-field">Postcode<input className="tc-input" value={pc} onChange={(e) => setPc(e.target.value.toUpperCase())} placeholder="e.g. RG21 7AB" disabled={!canChange} /></label>
        {canChange && <Button onClick={() => void save()} disabled={(pc.trim() || null) === (site.postcode ?? null)}>Save</Button>}
      </div>
      <small className="muted">{site.lat !== null && site.lng !== null ? `On the map (${site.lat.toFixed(3)}, ${site.lng.toFixed(3)}) for the weather card.` : "Not on the map yet: add the postcode (or link the site to Fuel Finder)."}</small>
    </div>
  );
}

/** How other systems know the site, in words ("Fuel Finder, SM365, Shell supply") rather than raw keys. */
function linkNames(keys: string[]): string {
  const named = keys.map((k) => k.startsWith("supplier:") ? `${k.slice(9).replace(/^./, (c) => c.toUpperCase())} supply`
    : k.startsWith("fuel-finder") ? "Fuel Finder" : k === "sm365" ? "SM365" : k.replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase()));
  return [...new Set(named)].join(", ");
}

interface SiteProject { id: string; name: string; status: string; healthWords: string | null; health: string | null; pctDone: number; targetOn: string | null; owner: string; overdue: number; link: string; nextMilestone: { title: string; due_on: string } | null }
const HEALTH_PILL: Record<string, "success" | "warning" | "critical"> = { on_track: "success", at_risk: "warning", off_track: "critical" };
/** The site's projects (refits, works, openings) from Projects. Not shown when Projects is off or there are none. */
function SiteProjects({ code }: { code: string }) {
  const { navigate } = useShell();
  const [list, setList] = useState<SiteProject[] | null>(null);
  useEffect(() => { setList(null); void api<{ projects: SiteProject[] | null }>(`/api/m/sites/site/${code}/projects`).then((d) => setList(d.projects), () => setList(null)); }, [code]);
  if (!list?.length) return null;
  return (
    <div className="tc-mt">
      <h3 className="tc-h3">Projects</h3>
      <div className="tc-mini-list">
        {list.map((p) => (
          <button key={p.id} className="tc-mini-row" onClick={() => navigate(p.link)}>
            <span className="mono">{Math.round(p.pctDone * 100)}%</span>
            <span><b>{p.name}</b><br /><span className="muted tc-small">{p.owner}{p.nextMilestone ? ` · next: ${p.nextMilestone.title} ${ukDate(p.nextMilestone.due_on)}` : p.targetOn ? ` · target ${ukDate(p.targetOn)}` : ""}{p.overdue ? ` · ${p.overdue} overdue` : ""}</span></span>
            {p.health ? <StatusPill status={HEALTH_PILL[p.health] ?? "info"}>{p.healthWords}</StatusPill> : <span />}
          </button>
        ))}
      </div>
    </div>
  );
}
